import React from 'react';
import cx from 'classnames';

import { IconFlag, ItemLabel } from '@xarvio/elements';
import { Text } from '@xarvio/corporate-styles';
import { Row, RowCell } from '@xarvio/layout';
import { TranslateMessage } from '@xarvio/utils';
import { getCountryAlternativeFlag } from '@xarvio/frontend-core-platform/lib/country/CountryUtils';
import type { TranslationId } from '@xarvio/frontend-core-platform/lib/types/TranslationId';

import styles from './CountryLanguageItem.module.scss';

export interface CountryLanguageItemProps {
  className?: string;
  countryClassName?: string;
  languageClassName?: string;
  showLanguage?: boolean;
  languageOnRight?: boolean;
  size?: 'medium' | 'small';
  countryTranslationKey: TranslationId;
  languageTranslationKey: TranslationId;
  countryCode: string;
}

export const CountryLanguageItem: React.FC<
  React.PropsWithChildren<CountryLanguageItemProps>
> = ({
  languageOnRight,
  showLanguage,
  size,
  className,
  countryClassName,
  languageClassName,
  countryTranslationKey,
  countryCode,
  languageTranslationKey,
}) => {
  const classNames = cx(
    styles.item,
    size && styles[`item_size-${size}`],
    languageOnRight && styles.item_languageOnRight,
    className,
  );

  const countryClassNames = cx(styles.country, countryClassName);
  const languageClassNames = cx(styles.language, languageClassName);

  const item = (
    <ItemLabel
      className={classNames}
      iconEl={
        <IconFlag
          country={getCountryAlternativeFlag(countryCode)}
          size={size === 'small' ? 16 : 24}
        />
      }
      title={
        <Text className={countryClassNames}>
          <TranslateMessage id={countryTranslationKey} />
        </Text>
      }
      {...(!languageOnRight &&
        showLanguage && {
          subTitle: (
            <Text className={languageClassNames}>
              <TranslateMessage id={languageTranslationKey} />
            </Text>
          ),
        })}
      noSidePaddings
    />
  );

  if (!languageOnRight) {
    return item;
  }

  return (
    <Row
      align={'center'}
      margin={'no'}
      gaps={'narrow'}
      noSideGaps
      className={classNames}
    >
      <RowCell>{item}</RowCell>
      {showLanguage && (
        <RowCell auto className={languageClassNames}>
          <TranslateMessage id={languageTranslationKey} />
        </RowCell>
      )}
    </Row>
  );
};
